import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useI18n } from "@wato/i18n/react";
import { useApi } from "../../api/context";
import type { AiAgentView } from "../../api/automation-types";
import type { KbListView } from "../../api/kb-types";
import { formatBasisPoints } from "../../pages/analytics/overview";
import { isoDayInZone } from "../../lib/analytics-range";
import { formatDate, mayUse } from "../roles/role-label";
import { useWorkspace } from "../workspace/WorkspaceContext";

/** Latest ingest time across sources (ISO) or null. */
export function lastTrainingAt(sources: KbListView["sources"]): string | null {
  let best: string | null = null;
  for (const s of sources) {
    const at = s.lastIngestedAt;
    if (at && (best === null || at > best)) best = at;
  }
  return best;
}

/** OWN-150a: AI agents on/off, knowledge sources, last training time, link to the AI page. */
export function HomeAiStatus({ workspaceId }: { readonly workspaceId: string }) {
  const { t, locale } = useI18n();
  const api = useApi();
  const [state, setState] = useState<{ agents: AiAgentView[]; kb: KbListView } | "failed" | null>(
    null,
  );
  const { workspace } = useWorkspace();
  const canViewAnalytics = mayUse(workspace, "analytics.view");
  // OWN-219 B: replied-by-AI ring (today's automation rate); stays hidden when unknown.
  const [rate, setRate] = useState<number | null>(null);
  useEffect(() => {
    if (!canViewAnalytics) return;
    let live = true;
    const day = isoDayInZone(new Date());
    Promise.resolve()
      .then(() => api.getAnalyticsOverview(workspaceId, { from: day, to: day }))
      .then(
        (o) => live && setRate(o.automation.rateBasisPoints),
        () => undefined,
      );
    return () => {
      live = false;
    };
  }, [api, workspaceId, canViewAnalytics]);
  const fmt = useMemo(
    () => new Intl.NumberFormat(locale === "ar" ? "ar-u-nu-latn" : "en"),
    [locale],
  );

  useEffect(() => {
    let live = true;
    Promise.all([api.listAiAgents(workspaceId), api.listKnowledgeSources(workspaceId)]).then(
      ([agents, kb]) => live && setState({ agents, kb }),
      () => live && setState("failed"),
    );
    return () => {
      live = false;
    };
  }, [api, workspaceId]);

  const last = state && state !== "failed" ? lastTrainingAt(state.kb.sources) : null;
  const on =
    state && state !== "failed" ? state.agents.filter((a) => a.status === "published") : [];
  return (
    <section
      aria-label={t("dashboard.home.aiStatus.title")}
      data-testid="home-ai-status"
      className="flex flex-col gap-5 rounded-2xl border border-app-line bg-app-surface p-6"
    >
      <div className="flex items-center gap-2.5">
        <h3 className="m-0 flex-1 text-lg font-semibold text-app-fg">
          {t("dashboard.home.aiStatus.title")}
        </h3>
        {state && state !== "failed" && state.agents.length > 0 ? (
          <span
            data-testid="home-ai-switch"
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${on.length > 0 ? "bg-app-accent-soft text-app-accent-fg" : "bg-app-surface-2 text-app-muted"}`}
          >
            <span
              aria-hidden="true"
              className={`size-2 rounded-full ${on.length > 0 ? "bg-app-accent" : "bg-app-muted"}`}
            />
            {t(on.length > 0 ? "dashboard.home.today.aiOn" : "dashboard.home.today.aiOff")}
          </span>
        ) : null}
      </div>
      {rate !== null ? (
        <div className="flex items-center gap-5" data-testid="home-ai-ring">
          <div
            aria-hidden="true"
            style={{ ["--p" as string]: Math.min(100, Math.max(0, rate / 100)) }}
            className="flex size-28 shrink-0 items-center justify-center rounded-full [background:conic-gradient(var(--color-app-accent)_calc(var(--p)*1%),var(--color-app-surface-2)_0)]"
          >
            <div className="flex size-[5.5rem] items-center justify-center rounded-full bg-app-surface text-2xl font-bold text-app-fg tabular-nums">
              {formatBasisPoints(rate)}
            </div>
          </div>
          <p className="m-0 text-sm text-app-muted">{t("dashboard.home.today.aiReplied")}</p>
        </div>
      ) : null}
      {state === "failed" ? (
        <p className="text-sm text-app-muted">{t("dashboard.home.aiStatus.unavailable")}</p>
      ) : state ? (
        <dl className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(9rem,1fr))]">
          <div>
            <dt className="text-xs text-app-muted">{t("dashboard.home.aiStatus.agents")}</dt>
            <dd data-testid="home-ai-agents" className="text-base font-semibold text-app-fg">
              {state.agents.length === 0
                ? t("dashboard.home.aiStatus.noAgents")
                : t("dashboard.home.aiStatus.agentsOn", {
                    on: fmt.format(on.length),
                    total: fmt.format(state.agents.length),
                  })}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-app-muted">{t("dashboard.home.aiStatus.sources")}</dt>
            <dd data-testid="home-ai-sources" className="text-base font-semibold text-app-fg">
              {fmt.format(state.kb.sources.length)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-app-muted">{t("dashboard.home.aiStatus.lastTraining")}</dt>
            <dd data-testid="home-ai-last" className="text-base font-semibold text-app-fg">
              {last ? formatDate(last, locale) : t("dashboard.home.aiStatus.never")}
            </dd>
          </div>
        </dl>
      ) : null}
      <Link
        to={`/w/${workspaceId}/ai`}
        className="text-sm font-medium text-app-accent-fg underline"
      >
        {t("dashboard.home.aiStatus.open")}
      </Link>
    </section>
  );
}
